import { Bar, CartesianGrid, Cell, ComposedChart, LabelList, Legend, Line, ReferenceLine, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { axisFmt, tipFmt } from "@/lib/format";

const COLORS = ["var(--chart-navy)", "#F5A623", "#10B981", "#EF4444", "#6366F1", "#0EA5E9", "#D946EF", "#84CC16",
  "#F97316", "#14B8A6", "#A855F7", "#E11D48", "#64748B", "#CA8A04", "#2563EB", "#059669", "#DB2777", "#7C3AED"];
const PREV = "#94A3B8";

const colorsOf = (series) => {
  let j = 0;
  return series.map((s) => s.color || (s.prev ? PREV : COLORS[j++ % COLORS.length]));
};

export const TrendChart = ({ trend }) => {
  if (!trend) return null;
  const ax = axisFmt(trend);
  const colors = colorsOf(trend.series);
  const label = (s) => s.nameKey
    ? <LabelList dataKey={s.nameKey} position="top" style={{ fontSize: 9, fill: "currentColor" }} />
    : s.stack ? <LabelList dataKey={s.key} position="center" formatter={(v) => (v ? ax(v) : "")} style={{ fontSize: 9, fill: s.prev ? "#1E293B" : "#fff" }} />
    : s.labeled && <LabelList dataKey={s.key} position="top" formatter={ax} style={{ fontSize: 9, fill: "currentColor" }} />;
  const nk = trend.series.find((s) => s.nameKey)?.nameKey;
  const cats = nk ? [...new Set(trend.data.map((d) => d[nk]).filter(Boolean))] : [];
  const tip = tipFmt(trend);
  const tipFormatter = nk ? (v, n, item) => [tip(v), item?.payload?.[nk] || n] : tip;
  return (
    <div data-testid="trend-chart" className="rounded-xl border bg-card p-4 sm:p-5 shadow-sm">
      <h3 className="font-heading text-base sm:text-lg font-semibold">{trend.title}{trend.scale ? " (Rp Juta)" : ""}</h3>
      <p className="text-xs text-muted-foreground">{trend.subtitle || "Tren bulanan tahun terpilih vs tahun sebelumnya"}</p>
      <div className="mt-4 h-72">
        <ResponsiveContainer width="100%" height="100%">
          <ComposedChart data={trend.data} margin={{ top: nk ? 28 : 8, right: 8, left: 0, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="currentColor" className="text-slate-200 dark:text-slate-700" vertical={false} />
            <XAxis dataKey="bulan" tickLine={false} axisLine={false}
              {...(trend.x_label ? { interval: 0, angle: -30, textAnchor: "end", height: 64, tick: { fontSize: 10 } } : { tick: { fontSize: 11 } })} />
            <YAxis tick={{ fontSize: 11 }} tickLine={false} axisLine={false} width={56} tickFormatter={ax} allowDecimals={trend.fmt !== "num"} />
            <Tooltip formatter={tipFormatter} contentStyle={{ borderRadius: 10, fontSize: 12 }} />
            {!nk && <Legend wrapperStyle={{ fontSize: 12 }} />}
            {(trend.refs || []).map((r) => (
              <ReferenceLine key={r.label} y={r.value} stroke="#EF4444" strokeDasharray="4 4" label={{ value: r.label, fontSize: 10, fill: "#EF4444", position: "insideTopRight" }} />
            ))}
            {trend.series.map((s, i) =>
              s.type === "bar" ? (
                <Bar key={s.key} dataKey={s.key} name={s.label} fill={colors[i]} stackId={s.stack}
                  radius={s.stack ? undefined : [4, 4, 0, 0]} maxBarSize={s.stack ? 34 : 28}>
                  {s.nameKey && trend.data.map((d) => <Cell key={d.bulan} fill={COLORS[Math.max(0, cats.indexOf(d[s.nameKey])) % COLORS.length]} />)}
                  {label(s)}
                </Bar>
              ) : (
                <Line key={s.key} dataKey={s.key} name={s.label} stroke={colors[i]} strokeWidth={2.2}
                  strokeDasharray={s.dashed ? "5 4" : undefined} dot={{ r: 2.5 }} connectNulls>{label(s)}</Line>
              )
            )}
          </ComposedChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};
